import { MouseEvent, useState } from 'react'
import { useTranslation } from 'react-i18next'

import { useIsSmallScreen } from 'hooks/useIsSmallScreen'
import { ManageListItem } from 'components/inputs/ManageListItem/ManageListItem'
import { Label, LabelType } from 'types/Label'
import { useHasRole } from 'hooks/useHasRoles'
import { ADMIN_ROLE } from 'constants/roleGroups'
import * as Styled from './LabelsListItem.styles'

interface LabelsListItemProps {
  label: Label
  onDelete?: (id: number) => void
  onEdit?: (label: Label) => void
  className?: string
}

const LabelsListItem: React.FC<LabelsListItemProps> = ({
  label,
  onDelete: handleDelete,
  onEdit: handleEdit,
  className,
}) => {
  const { t, i18n: { language: userLanguageBrowserCode } } = useTranslation()
  const isSmallScreen = useIsSmallScreen()
  const isAdmin = useHasRole(ADMIN_ROLE)
  const [menuAnchorEl, setMenuAnchorEl] = useState<HTMLElement | null>(null)

  const handleMenuOpen = (event: MouseEvent<HTMLElement>) => setMenuAnchorEl(event.currentTarget)

  const handleMenuClose = () => setMenuAnchorEl(null)

  const canEdit = label?.type === LabelType.USER_DEFINED || isAdmin

  return (
    <Styled.ManageListItem className={className} active $borderColor={label.color}>
      <ManageListItem.FieldsContainer $rows={3}>
        {isSmallScreen && (
          <>
            <ManageListItem.HeaderText variant='subtitle1'>
              {t('common.id')}
            </ManageListItem.HeaderText>
            <ManageListItem.HeaderText variant='subtitle1'>
              {t('common.name')}
            </ManageListItem.HeaderText>
            <ManageListItem.HeaderText variant='subtitle1'>
              {t('common.type')}
            </ManageListItem.HeaderText>
          </>
        )}
        <Styled.IdText>
          {label.id}
        </Styled.IdText>
        <ManageListItem.TextField
          value={
            label.nameTranslations.find(
              ({ languageCode }) => languageCode === userLanguageBrowserCode
            )?.value || label.name
          }
          disabled
          $border={false}
          $active
        />
        <ManageListItem.TextField
          value={t(`labels.types.${[label.type]}`)}
          disabled
          $border={false}
          $active
        />
        <ManageListItem.ActionsContainer>
          {isSmallScreen ? (
            <>
              <ManageListItem.EditIconButton
                tooltip={t('common.edit')}
                disabled={!canEdit}
                onClick={() => handleEdit?.(label)}
              />
              <ManageListItem.DeleteIconButton
                tooltip={t('common.delete')}
                disabled={!canEdit}
                onClick={() => handleDelete?.(label.id)}
              />
            </>
          ) : (
            <>
              <ManageListItem.MenuIconButton
                onClick={handleMenuOpen}
              />
              <ManageListItem.Menu
                open={!!menuAnchorEl}
                anchorEl={menuAnchorEl}
                onClose={handleMenuClose}
              >
                <ManageListItem.MenuItem
                  disabled={!canEdit}
                  onClick={() => handleEdit?.(label)}
                >
                  {t('common.edit')}
                </ManageListItem.MenuItem>
                <ManageListItem.MenuItem
                  disabled={!canEdit}
                  onClick={() => handleDelete?.(label.id)}
                >
                  {t('common.delete')}
                </ManageListItem.MenuItem>
              </ManageListItem.Menu>
            </>
          )}
        </ManageListItem.ActionsContainer>
      </ManageListItem.FieldsContainer>
    </Styled.ManageListItem>
  )
}

export { LabelsListItem }
